import { isArray, isEmpty, orderBy, find } from 'lodash';
import * as actions from '../store/actions';
import { pipelinePagesTypes } from '../../../shared/constants/enums/pipelinePagesTypes';

export const generateColumns = (buckets, statusPropName, isNumber) => {
  const colsFromBackend = {};

  buckets.forEach((bucket, index) => {
    const columnObjectName = `${isNumber ? bucket[statusPropName].toString() : bucket[statusPropName]}`;

    const col = {
      [columnObjectName]: {
        name: bucket.name,
        status: bucket[statusPropName],
        items: [],
        hasMore: false,
        hasMoreLoading: false,
        loading: true,
        pageSize: 10,
        pageNumber: 1,
        visible: true,
        order: index + 1,
      },
    };

    Object.assign(colsFromBackend, col);
  });

  return colsFromBackend;
};

export const generateStageColumns = (configColumns, stagesArray) => {
  const colsFromBackend = {};

  const stagesFromConfig = (configColumns && configColumns.filter((col) => stagesArray.includes(col.id))) || [];

  const orderedConfig = orderBy(stagesFromConfig, ['order'], ['asc']) || [];

  orderedConfig.forEach((stage) => {
    const columnObjectName = stage.id;

    const col = {
      [columnObjectName]: {
        name: columnObjectName,
        stage: columnObjectName,
        items: [],
        hasMore: false,
        hasMoreLoading: false,
        loading: true,
        pageSize: 10,
        pageNumber: 1,
        visible: stage.visible,
        order: stage.order,
      },
    };

    Object.assign(colsFromBackend, col);
  });

  let prevOrder = orderedConfig?.length > 0 ? orderedConfig[orderedConfig.length - 1].order : 0;

  stagesArray.forEach((stage) => {
    const columnObjectName = stage;

    const col = {
      [columnObjectName]: {
        name: columnObjectName,
        stage: columnObjectName,
        items: [],
        hasMore: false,
        hasMoreLoading: false,
        loading: true,
        pageSize: 10,
        pageNumber: 1,
        order: ++prevOrder,
        visible: prevOrder <= 6,
      },
    };

    const exists = find(orderedConfig, (s) => s.id == columnObjectName);

    if (!exists) {
      Object.assign(colsFromBackend, col);
    }
  });

  return colsFromBackend;
};

export const generateCustomTypeColumns = (configColumns, customTypesArray) => {
  const colsFromBackend = {};

  const customTypesFromConfig = (configColumns && configColumns.filter((col) => customTypesArray.includes(col.id))) || [];

  const orderedConfig = orderBy(customTypesFromConfig, ['order'], ['asc']) || [];

  orderedConfig.forEach((customType) => {
    const columnObjectName = customType.id;

    const col = {
      [columnObjectName]: {
        name: columnObjectName,
        customType: columnObjectName,
        items: [],
        hasMore: false,
        hasMoreLoading: false,
        loading: true,
        pageSize: 10,
        pageNumber: 1,
        visible: customType.visible,
        order: customType.order,
      },
    };

    Object.assign(colsFromBackend, col);
  });

  let prevOrder = orderedConfig?.length > 0 ? orderedConfig[orderedConfig.length - 1].order : 0;

  customTypesArray.forEach((customType) => {
    const columnObjectName = customType;

    const col = {
      [columnObjectName]: {
        name: columnObjectName,
        customType: columnObjectName,
        items: [],
        hasMore: false,
        hasMoreLoading: false,
        loading: true,
        pageSize: 10,
        pageNumber: 1,
        order: ++prevOrder,
        visible: prevOrder <= 6,
      },
    };

    const exists = find(orderedConfig, (s) => s.id == columnObjectName);

    if (!exists) {
      Object.assign(colsFromBackend, col);
    }
  });

  return colsFromBackend;
};

export const loadData = async (columnsFromBackend, resetPaging, filters, actionName, serviceRequestType) => {
  const updatedColumns = { ...columnsFromBackend };

  const callStack = [];

  Object.keys(updatedColumns).forEach((col) => {
    if (resetPaging) updatedColumns[col].pageNumber = 1;

    updatedColumns[col].loading = true;
    callStack.push(getCallStackItem(filters, updatedColumns[col], serviceRequestType));
  });

  const getPropNameByType = () => {
    switch (serviceRequestType) {
      case pipelinePagesTypes.ServiceRequestsCustomTypesOverview:
        return 'customType';
      case pipelinePagesTypes.ServiceRequestsStagesOverview:
        return 'stage';
      default:
        return 'status';
    }
  };

  const propName = getPropNameByType();

  if (callStack.length) {
    const result = await fetchData(
      actions[actionName]({ data: callStack, pipelineType: serviceRequestType }),
      updatedColumns,
      propName,
      null,
      serviceRequestType,
    ); 
    return result;
  } else {
    return;
  }
};

export const fetchData = async (callStack, updatedColumns, statusPropName, shouldUpdateTotal, serviceRequestType) => {
  const results = await callStack.then((values) => {
    Object.keys(updatedColumns).forEach((colKey) => {
      const result = values.find((item) => {
        const updatedColValue =
          serviceRequestType === pipelinePagesTypes.OpportunitiesOverview || serviceRequestType === pipelinePagesTypes.RenewalOverview
            ? updatedColumns[colKey]['status']
            : updatedColumns[colKey][statusPropName];

        if (item?.data?.length > 0 && item.data[0][statusPropName] === updatedColValue) {
          return item;
        } else {
          return null;
        }
      });

      if (result) {
        updatedColumns[colKey].items = result.data || [];
        updatedColumns[colKey].totalCount = result.totalCount;
        updatedColumns[colKey].hasMore = result.hasMore || false;
      } else {
        updatedColumns[colKey].items = [];
        updatedColumns[colKey].totalCount = 0;
        updatedColumns[colKey].hasMore = false;
      }

      if (shouldUpdateTotal) {
        updateColumnTotal(updatedColumns, colKey);
      }

      updatedColumns[colKey].loading = false;
    });

    return updatedColumns;
  });

  return results;
};

export const pipelineDateFormat = 'MMM DD YYYY';

const getCallStackItem = (filters, columnItem, serviceRequestType) => {
  return {
    pageNumber: columnItem.pageNumber,
    pageSize: columnItem.pageSize,
    status: filters && filters.status >= 0 ? filters.status : columnItem.status,
    stage: filters?.stage ? filters.stage : columnItem.stage,
    customType: filters?.customType ? filters.customType : columnItem.customType,
    agentIds: filters ? filters.agentIds : [],
    serviceRequestTypes: filters && filters.serviceRequestTypes ? filters.serviceRequestTypes : [],
    subject: filters && filters.subject ? filters.subject : '',
    visibleOnBoard: columnItem.visible,

    ticketTitle: filters?.ticketTitle && serviceRequestType === pipelinePagesTypes.TaskOverview ? filters?.ticketTitle.trim() : null,
    tagIds: filters?.tagIds?.length && serviceRequestType === pipelinePagesTypes.TaskOverview ? filters?.tagIds : null,
    statusIds: filters?.statusIds?.length && serviceRequestType === pipelinePagesTypes.TaskOverview ? filters?.statusIds : null,
    lastChangeDateRange: filters?.lastChangeDateRange?.length && serviceRequestType === pipelinePagesTypes.TaskOverview ? filters?.lastChangeDateRange : null,
    
    insuredIds: filters?.insuredIds?.length ? filters?.insuredIds : null,
    dueDateRange: filters?.dueDateRange?.length ? filters?.dueDateRange : null,
  };
};

export const loadSingleBucket = async (columns, column, resetPage, filters, actionName, serviceRequestType) => {
  const updatedColumns = { ...columns };

  const columnKeyToEdit = Object.keys(updatedColumns).find((key) => updatedColumns[key].name === column.name);

  updateColumnStateBeforeRequest(updatedColumns, columnKeyToEdit, resetPage);

  const result = await actions[actionName]({
    data: [getCallStackItem(filters, updatedColumns[columnKeyToEdit], serviceRequestType)],
    pipelineType: serviceRequestType,
  }).then((result) => {
    if (isArray(result) && !isEmpty(result)) result = result[0];
    
    return updateColumnWithData(result, updatedColumns, resetPage, columnKeyToEdit);
  });

  return result;
};

export const updateColumnStateBeforeRequest = (updatedColumns, columnKeyToEdit, resetPage) => {
  if (resetPage) {
    updatedColumns[columnKeyToEdit].pageNumber = 1;
  } else {
    updatedColumns[columnKeyToEdit].pageNumber += 1;
    updatedColumns[columnKeyToEdit].hasMoreLoading = true;
  }
};

export const updateColumnWithData = (result, updatedColumns, resetPage, columnKeyToEdit, shouldUpdateTotal) => {
  if (result) {
    if (resetPage) {
      updatedColumns[columnKeyToEdit].items = result.data ? result.data : [];
    } else {
      const data = result.data ? result.data : [];
      updatedColumns[columnKeyToEdit].items.push(...data);
    }

    if (shouldUpdateTotal) {
      updateColumnTotal(updatedColumns, columnKeyToEdit);
    }

    updatedColumns[columnKeyToEdit].hasMore = result.hasMore || false;
    updatedColumns[columnKeyToEdit].totalCount = result.totalCount;
  }

  updatedColumns[columnKeyToEdit].hasMoreLoading = false;
  updatedColumns[columnKeyToEdit].loading = false;

  return updatedColumns;
};

const updateColumnTotal = (updatedColumns, columnKey) => {
  if (updatedColumns[columnKey].items && updatedColumns[columnKey].items.length) {
    updatedColumns[columnKey].total = updatedColumns[columnKey].items.reduce((a, b) => a + (b.estimatedRevenue || 0), 0);
  } else {
    updatedColumns[columnKey].total = 0;
  }
};
